import Phaser from 'phaser';
import type { RunStats, WaveDef } from '../core/types';
import { gameEvents } from '../core/events';
import { WaveEngine } from './WaveEngine';
import type { CombatContext, CombatRect } from './types';

export type { CombatContext, CombatRect } from './types';

/**
 * Punto de entrada del módulo de combate para el resto del juego. Se monta
 * en la franja superior de `GameScene` (~30% de alto, ver spec sección 2) y
 * expone una API mínima:
 *
 * - `mount(scene, rect)`: crea el contenedor visual dentro del rectángulo
 *   dado (coordenadas absolutas de escena).
 * - `startWave(def, ctx)`: lanza una oleada data-driven a partir de un
 *   `WaveDef` (ver `core/types.ts`) y del contexto de la run actual (stats,
 *   hp/maxHp persistidos, número de oleada).
 * - `update(time, delta)`: debe llamarse cada frame desde el `update()` de
 *   la escena — avanza marcha de enemigos, cooldowns de ataque y la fase de
 *   recolección. Sin esto, la oleada queda congelada.
 *
 * Toda la comunicación hacia fuera es por el bus de eventos tipado
 * (`combat:*`, ver `core/events.ts`) — no hay callbacks directos. Este
 * módulo NO conoce la máquina de estados del juego completo (eso es de
 * `GameScene`/F2): solo sabe pelear una oleada y avisar cuándo termina
 * limpia (`combat:waveCleared`) o cuándo muere el héroe
 * (`combat:heroDied`). La decisión de revivir (1 vez por run, al 30% de
 * vida según la spec) es de quien integra: debe llamar `reviveHero(hp)`.
 */
export class CombatView {
  private scene?: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private rect?: CombatRect;
  private waveEngine?: WaveEngine;
  private speed: 1 | 2 = 1;
  private unsubscribeSpeed?: () => void;

  /** Crea el contenedor de render dentro del rectángulo dado. Llamar una vez. */
  mount(scene: Phaser.Scene, rect: CombatRect): void {
    this.scene = scene;
    this.rect = rect;
    this.container = scene.add.container(0, 0);
    this.unsubscribeSpeed = gameEvents.on('ui:speedChanged', ({ speed }) => this.setSpeed(speed));
  }

  /** Lanza una nueva oleada data-driven a partir de un `WaveDef`. */
  startWave(def: WaveDef, ctx: CombatContext): void {
    if (!this.scene || !this.container || !this.rect) {
      throw new Error('CombatView.startWave: hay que llamar mount() primero');
    }
    this.waveEngine?.destroy();
    gameEvents.emit('combat:waveStart', { wave: ctx.wave, total: ctx.total });
    this.waveEngine = new WaveEngine(this.scene, this.container, this.rect, {
      def,
      stats: ctx.stats,
      hp: ctx.hp,
      maxHp: ctx.maxHp,
      wave: ctx.wave,
    });
  }

  /** Actualiza stats en caliente (p.ej. tras comprar una carta entre oleadas). */
  setStats(stats: RunStats): void {
    this.waveEngine?.setStats(stats);
  }

  /** Cambia la velocidad de simulación (×1 / ×2); también reacciona sola a `ui:speedChanged`. */
  setSpeed(speed: 1 | 2): void {
    this.speed = speed;
  }

  /**
   * Revive al héroe con la vida indicada (la política de "cuándo" y "con
   * cuánto %" es de quien integra, no de este módulo) y reanuda la oleada
   * en curso.
   */
  reviveHero(hp: number): void {
    this.waveEngine?.revive(hp);
  }

  /** Debe llamarse cada frame desde `GameScene.update()`. */
  update(_time: number, delta: number): void {
    this.waveEngine?.update(delta * this.speed);
  }

  /** true mientras haya una oleada en curso (peleando o recolectando bolas). */
  isWaveActive(): boolean {
    return this.waveEngine?.isActive() ?? false;
  }

  /** true si el héroe murió en la oleada actual (a la espera de revive o de `run:lost`). */
  isHeroDead(): boolean {
    return this.waveEngine?.isDead ?? false;
  }

  /** Vida actual del héroe (absoluta), para persistirla entre oleadas. Devuelve 0 si no hay oleada. */
  getHeroHp(): number {
    return this.waveEngine?.heroHp ?? 0;
  }

  /** Vida máxima efectiva del héroe en la oleada en curso. */
  getHeroMaxHp(): number {
    return this.waveEngine?.heroMaxHp ?? 0;
  }

  destroy(): void {
    this.waveEngine?.destroy();
    this.waveEngine = undefined;
    this.container?.destroy();
    this.container = undefined;
    this.unsubscribeSpeed?.();
  }
}
